/**
 * Convert a self-contained HTML document into a PDF — bypassing the
 * browser's print dialog entirely so there are never any "about:blank" /
 * URL / timestamp headers or footers stamped onto the file. The HTML is
 * rendered in a hidden iframe (so its inline styles, fonts, and images
 * resolve naturally) and `html2pdf.js` is dynamic-imported on demand so
 * it doesn't bloat the initial bundle.
 */

/**
 * Renders `html` into a hidden iframe and returns an html2pdf.js Worker
 * already chained up to `.from(doc.body)` with the shared export
 * settings, ready for `.save()`. Also returns the iframe so the caller
 * can remove it once the worker's promise settles.
 */
async function buildPdfWorker(html: string, filename: string): Promise<{ worker: any; iframe: HTMLIFrameElement }> {
  // The iframe gives the document its own scope (so the parent app's
  // CSS doesn't leak in), and lets the browser parse <head>, <style>,
  // and inline styles exactly as if it were a standalone page.
  const iframe = document.createElement('iframe')
  iframe.style.position = 'fixed'
  iframe.style.left = '-10000px'
  iframe.style.top = '0'
  // Width tuned to ~A4 at 96 DPI so the rendered layout doesn't get
  // squeezed to a phone-narrow viewport before html2canvas snapshots it.
  iframe.style.width = '794px'
  iframe.style.height = '0'
  iframe.style.border = '0'
  document.body.appendChild(iframe)

  await new Promise<void>((resolve, reject) => {
    iframe.addEventListener('load', () => resolve(), { once: true })
    iframe.addEventListener('error', () => reject(new Error('iframe load failed')), { once: true })
    iframe.srcdoc = html
  })

  const doc = iframe.contentDocument
  if (!doc) { iframe.remove(); throw new Error('iframe document not available') }

  // Drop anything marked "no-print" (e.g. an in-page "Download as PDF"
  // link) before snapshotting — html2canvas rasterizes the live DOM and
  // does not honor `@media print`, so without this the element would
  // otherwise bake permanently into the exported file.
  doc.querySelectorAll('.no-print').forEach((el) => el.remove())

  // Wait for any embedded images (logos, QR, charts) to finish so
  // html2canvas doesn't snapshot a half-loaded page.
  await Promise.all(Array.from(doc.images).map((img) =>
    img.complete
      ? Promise.resolve()
      : new Promise<void>((r) => {
          img.addEventListener('load', () => r(), { once: true })
          img.addEventListener('error', () => r(), { once: true })
        })
  ))

  // Resize the iframe to its content's full height before snapshotting
  // so the entire document is rasterised, not just the visible bit.
  const fullHeight = doc.documentElement.scrollHeight
  iframe.style.height = `${fullHeight}px`

  // Dynamic-import keeps html2pdf.js (~150 KB minified) out of the
  // initial bundle — only paid for when the user actually exports.
  const mod = await import('html2pdf.js') as { default: any }
  const html2pdf = mod.default

  const worker = html2pdf()
    .set({
      // No page margin — a template's own body padding/background is
      // what decides how much breathing room a report has (some, like
      // the built-in default, want to look like a card on a colored
      // backdrop; others want to run edge-to-edge). Adding a margin
      // here on top of that would double up as an extra, uncontrollable
      // white border baked around every page regardless of what the
      // template itself asks for.
      margin: 0,
      filename,
      // scale 2 + quality 0.92 costs roughly 2x the file size of
      // scale 1.5/quality 0.8, but reports routinely carry real
      // evidentiary photos (thermal imaging, inspection close-ups) that
      // readers zoom into — at the lower setting those visibly
      // pixelate/JPEG-block on zoom even though the surrounding text
      // still looks fine at a glance.
      image: { type: 'jpeg', quality: 0.92 },
      html2canvas: {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
        windowWidth: 794,
      },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      pagebreak: { mode: ['css', 'legacy'] },
    })
    .from(doc.body)

  return { worker, iframe }
}

export async function downloadPdf(html: string, filename: string) {
  const { worker, iframe } = await buildPdfWorker(html, filename)
  try {
    await worker.save()
  } finally {
    iframe.remove()
  }
}

/**
 * Same rendering pipeline as `downloadPdf`, but returns the raw PDF
 * bytes instead of triggering a browser download — for callers building
 * a zip of many reports (JSZip) or merging several into one combined
 * PDF (pdf-lib), e.g. the Settings → Data Export bulk completion-report
 * export.
 */
export async function htmlToPdfBytes(html: string, filename = 'report.pdf'): Promise<ArrayBuffer> {
  const { worker, iframe } = await buildPdfWorker(html, filename)
  try {
    return await worker.outputPdf('arraybuffer')
  } finally {
    iframe.remove()
  }
}

/**
 * Triggers a browser download of already-rendered bytes (a real
 * server-generated PDF, typically — see api/src/lib/htmlToPdf.js) —
 * no rendering happens here, just object-URL → invisible-anchor →
 * click → revoke, same pattern used for CSV/zip exports elsewhere in
 * the app. Has to run on the same tick as the triggering user gesture
 * for Safari to allow the download.
 */
export function downloadBytes(bytes: ArrayBuffer, filename: string, mimeType = 'application/pdf') {
  const url = URL.createObjectURL(new Blob([bytes], { type: mimeType }))
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}
